import React, { useEffect, useMemo, useState } from 'react';
import { Container, Form, Button, Toast, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { addProject, updateProject } from '../../services/projectService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const DEFAULT_CATEGORY = 'genel';

const INITIAL_FORM_STATE = {
  title: '',
  summary: '',
  content: '',
  projectDate: '',
  status: 'Devam Ediyor',
  category: DEFAULT_CATEGORY,
  image: null,
  beforeImages: [],
  galleryImages: [],
  afterImages: [],
  videos: [],
  beforeVideos: [],
  afterVideos: [],
  videoFiles: [],
  beforeVideoFiles: [],
  afterVideoFiles: []
};

const INITIAL_EXISTING_MEDIA = {
  beforeImages: [],
  galleryImages: [],
  afterImages: []
};

const videoFileKeyMap = {
  videos: 'videoFiles',
  beforeVideos: 'beforeVideoFiles',
  afterVideos: 'afterVideoFiles'
};

const normalizeContent = (content) => {
  if (!content) return '';
  if (Array.isArray(content)) {
    return content.join('\n\n');
  }
  return `${content}`;
};

const resolveArray = (...sources) => {
  for (const source of sources) {
    if (Array.isArray(source) && source.length > 0) {
      return source;
    }
  }
  return [];
};

const formatDateForInput = (value) => {
  if (!value) return '';

  if (typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    const [day, month, year] = value.split('.');
    return `${year}-${month}-${day}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ProjectForm = ({
  editingProject,
  onSubmit,
  onCancel,
  isModal = false,
  initialCategory = DEFAULT_CATEGORY
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(() => ({
    ...INITIAL_FORM_STATE,
    category: initialCategory ?? DEFAULT_CATEGORY
  }));
  const [existingMedia, setExistingMedia] = useState(INITIAL_EXISTING_MEDIA);
  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    variant: 'success',
    message: ''
  });

  const isEditing = Boolean(editingProject?.guid);

  useEffect(() => {
    if (!editingProject) {
      setFormData({
        ...INITIAL_FORM_STATE,
        category: initialCategory ?? DEFAULT_CATEGORY
      });
      setExistingMedia(INITIAL_EXISTING_MEDIA);
      return;
    }

    setFormData({
      title: editingProject.title ?? '',
      summary: editingProject.summary ?? '',
      content: normalizeContent(editingProject.content),
      projectDate:
        editingProject.projectDate
          ? formatDateForInput(editingProject.projectDate)
          : formatDateForInput(editingProject.date),
      status: editingProject.status ?? 'Devam Ediyor',
      category: editingProject.category ?? DEFAULT_CATEGORY,
      image: null,
      beforeImages: [],
      galleryImages: [],
      afterImages: [],
      videos: resolveArray(editingProject.videos),
      beforeVideos: resolveArray(editingProject.beforeVideos),
      afterVideos: resolveArray(editingProject.afterVideos),
      videoFiles: [],
      beforeVideoFiles: [],
      afterVideoFiles: []
    });

    setExistingMedia({
      beforeImages: resolveArray(editingProject.beforeImages),
      galleryImages: resolveArray(editingProject.galleryImages, editingProject.gallery),
      afterImages: resolveArray(editingProject.afterImages)
    });
  }, [editingProject, initialCategory]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleMultipleFileChange = (event, fieldName) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], ...files]
    }));
  };

  const removePendingFile = (fieldName, index) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, idx) => idx !== index)
    }));
  };

  const removeExistingImage = (fieldName, index) => {
    setExistingMedia((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, idx) => idx !== index)
    }));
  };

  const handleVideoChange = (event, fieldName) => {
    const raw = event.target.value.split('\n');
    const urls = raw
      .map((value) => value.trim())
      .filter((value, idx, arr) => value.length > 0 && arr.indexOf(value) === idx);

    setFormData((prev) => ({
      ...prev,
      [fieldName]: urls
    }));
  };

  const handleVideoFileChange = (event, fieldName) => {
    const key = videoFileKeyMap[fieldName];
    if (!key) {
      return;
    }

    const files = Array.from(event.target.files ?? []);
    if (!files.length) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [key]: [...(prev[key] ?? []), ...files]
    }));
  };

  const removeVideoFile = (fieldName, index) => {
    const key = videoFileKeyMap[fieldName];
    if (!key) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [key]: (prev[key] ?? []).filter((_, idx) => idx !== index)
    }));
  };

  const showToast = (message, variant = 'success') => {
    setToastState({
      show: true,
      message,
      variant
    });
  };

  const hideToast = () => {
    setToastState((prev) => ({
      ...prev,
      show: false
    }));
  };

  const resetForm = () => {
    setFormData({
      ...INITIAL_FORM_STATE,
      category: initialCategory ?? DEFAULT_CATEGORY
    });
    setExistingMedia(INITIAL_EXISTING_MEDIA);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const payload = {
      title: formData.title,
      summary: formData.summary,
      content: formData.content,
      projectDate: formData.projectDate,
      status: formData.status,
      category: formData.category,
      image: formData.image,
      beforeImages: formData.beforeImages,
      galleryImages: formData.galleryImages,
      afterImages: formData.afterImages,
      videos: formData.videos,
      beforeVideos: formData.beforeVideos,
      afterVideos: formData.afterVideos,
      videoFiles: formData.videoFiles,
      beforeVideoFiles: formData.beforeVideoFiles,
      afterVideoFiles: formData.afterVideoFiles,
      existingVideoUrls: formData.videos,
      existingBeforeVideoUrls: formData.beforeVideos,
      existingAfterVideoUrls: formData.afterVideos,
      existingBeforeImageUrls: existingMedia.beforeImages,
      existingGalleryImageUrls: existingMedia.galleryImages,
      existingAfterImageUrls: existingMedia.afterImages
    };

    try {
      const response = isEditing
        ? await updateProject({ ...payload, guid: editingProject.guid })
        : await addProject(payload);

      if (response?.isSuccess) {
        showToast(isEditing ? 'Proje başarıyla güncellendi.' : 'Proje başarıyla eklendi.');

        if (onSubmit) {
          onSubmit(response);
        }

        if (!isModal) {
          setTimeout(() => {
            navigate('/projects');
          }, 1000);
        } else {
          resetForm();
        }
      } else {
        showToast(response?.resultMessage ?? 'İşlem sırasında bir hata oluştu.', 'danger');
      }
    } catch (error) {
      console.error('Error submitting project:', error);
      showToast(error.message ?? 'İşlem sırasında bir hata oluştu.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const imageSections = useMemo(
    () => [
      {
        key: 'beforeImages',
        title: 'Proje Görselleri',
        description: 'Proje görsellerini seçin (birden fazla seçebilirsiniz)'
      },
      {
        key: 'galleryImages',
        title: 'Yapım Aşaması Görselleri',
        description: 'Yapım aşaması görsellerini seçin (birden fazla seçebilirsiniz)'
      },
      {
        key: 'afterImages',
        title: 'Bitmiş Hali Görselleri',
        description: 'Bitmiş hali görsellerini seçin (birden fazla seçebilirsiniz)'
      }
    ],
    []
  );

  const renderExistingImages = (fieldName) => {
    const images = existingMedia[fieldName] ?? [];
    if (!images.length) return null;

    return (
      <div className="mt-2">
        <small className="text-success fw-bold">Mevcut Görseller</small>
        {images.map((url, index) => (
          <div
            key={`${fieldName}-existing-${index}`}
            className="d-flex align-items-center justify-content-between bg-success bg-opacity-10 p-2 mb-1 rounded"
          >
            <div className="d-flex align-items-center">
              <img
                src={url}
                alt={`Mevcut görsel ${index + 1}`}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 4,
                  marginRight: 8
                }}
              />
              <span className="text-truncate" style={{ maxWidth: 180 }}>
                {`Görsel ${index + 1}`}
              </span>
            </div>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => removeExistingImage(fieldName, index)}
            >
              Sil
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const renderPendingFiles = (fieldName) => {
    const files = formData[fieldName] ?? [];
    if (!files.length) return null;

    return (
      <div className="mt-2">
        <small className="text-primary fw-bold">Yeni Seçilen Görseller</small>
        {files.map((file, index) => (
          <div
            key={`${fieldName}-pending-${index}`}
            className="d-flex align-items-center justify-content-between bg-primary bg-opacity-10 p-2 mb-1 rounded"
          >
            <span className="text-truncate" style={{ maxWidth: 180 }}>
              {file.name}
            </span>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => removePendingFile(fieldName, index)}
            >
              Sil
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const renderVideoFiles = (fieldName, label) => {
    const key = videoFileKeyMap[fieldName];
    if (!key) return null;

    const files = formData[key] ?? [];
    if (!files.length) return null;

    return (
      <div className="mt-2">
        <small className="text-primary fw-bold">{label}</small>
        {files.map((file, index) => (
          <div
            key={`${fieldName}-video-file-${index}`}
            className="d-flex align-items-center justify-content-between bg-primary bg-opacity-10 p-2 mb-1 rounded"
          >
            <span className="text-truncate" style={{ maxWidth: 220 }}>
              {file.name}
            </span>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => removeVideoFile(fieldName, index)}
            >
              Sil
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const formBody = (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label>Proje Başlığı *</Form.Label>
        <Form.Control
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          placeholder="Proje başlığını girin"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Özet</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="summary"
          value={formData.summary}
          onChange={handleInputChange}
          placeholder="Proje özetini girin"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Proje Tarihi</Form.Label>
        <Form.Control
          type="date"
          name="projectDate"
          value={formData.projectDate}
          onChange={handleInputChange}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Durum</Form.Label>
        <Form.Select name="status" value={formData.status} onChange={handleInputChange}>
          <option value="Devam Ediyor">Devam Ediyor</option>
          <option value="Proje Bitti">Proje Bitti</option>
          <option value="İptal Edildi">İptal Edildi</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Kategori</Form.Label>
        <Form.Select name="category" value={formData.category} onChange={handleInputChange}>
          <option value="genel">Genel</option>
          <option value="iskoop">İSKOOP</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Ana Proje Görseli</Form.Label>
        <Form.Control
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          required={!isEditing}
        />
        {isEditing && editingProject?.image && (
          <div className="mt-2 d-flex align-items-center">
            <img
              src={editingProject.image}
              alt="Mevcut kapak"
              style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, marginRight: 12 }}
            />
            <span className="text-muted">Mevcut ana görsel</span>
          </div>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Detaylı Açıklama</Form.Label>
        <div className="rich-text-editor-wrapper">
          <ReactQuill
            theme="snow"
            value={formData.content}
            onChange={(value) => {
              setFormData(prev => ({
                ...prev,
                content: value
              }));
            }}
            modules={{
              toolbar: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'font': [] }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'script': 'sub'}, { 'script': 'super' }],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
                [{ 'align': [] }],
                ['link', 'image', 'video'],
                ['clean'],
                ['code-block']
              ]
            }}
            style={{ height: '400px' }}
          />
        </div>
      </Form.Group>

      {imageSections.map(({ key, title, description }, index) => (
        <div key={key} className="mb-4" style={index === 0 ? { marginTop: '5rem' } : {}}>
          <h5 className="mb-3">{title}</h5>
          <Form.Group>
            <Form.Label className="visually-hidden">Dosya Seç</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => handleMultipleFileChange(event, key)}
              className="mb-2"
            />
            <Form.Text className="text-muted">{description}</Form.Text>
            {renderExistingImages(key)}
            {renderPendingFiles(key)}
          </Form.Group>
        </div>
      ))}

      <div className="mb-4">
        <h5 className="mb-2">Video URL'leri</h5>
      <Form.Group className="mb-3">
        <Form.Label>Proje Videoları</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Her satıra bir video URL'i girin"
          value={formData.beforeVideos.join('\n')}
          onChange={(event) => handleVideoChange(event, 'beforeVideos')}
        />
        <Form.Control
          className="mt-2"
          type="file"
          accept="video/*"
          multiple
          onChange={(event) => handleVideoFileChange(event, 'beforeVideos')}
        />
        <Form.Text className="text-muted">
          Dosya seçerek video yükleyebilir veya yukarıya URL girebilirsiniz.
        </Form.Text>
        {renderVideoFiles('beforeVideos', 'Yeni Yüklenen Videolar')}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Yapım Aşaması Videoları</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Her satıra bir video URL'i girin"
          value={formData.videos.join('\n')}
          onChange={(event) => handleVideoChange(event, 'videos')}
        />
        <Form.Control
          className="mt-2"
          type="file"
          accept="video/*"
          multiple
          onChange={(event) => handleVideoFileChange(event, 'videos')}
        />
        <Form.Text className="text-muted">
          Dosya seçerek video yükleyebilir veya yukarıya URL girebilirsiniz.
        </Form.Text>
        {renderVideoFiles('videos', 'Yeni Yüklenen Videolar')}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Bitmiş Hali Videoları</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Her satıra bir video URL'i girin"
          value={formData.afterVideos.join('\n')}
          onChange={(event) => handleVideoChange(event, 'afterVideos')}
        />
        <Form.Control
          className="mt-2"
          type="file"
          accept="video/*"
          multiple
          onChange={(event) => handleVideoFileChange(event, 'afterVideos')}
        />
        <Form.Text className="text-muted">
          Dosya seçerek video yükleyebilir veya yukarıya URL girebilirsiniz.
        </Form.Text>
        {renderVideoFiles('afterVideos', 'Yeni Yüklenen Videolar')}
      </Form.Group>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          variant="secondary"
          onClick={() => {
            if (onCancel) {
              onCancel();
            } else {
              navigate('/projects');
            }
          }}
          disabled={loading}
        >
          İptal
        </Button>
        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {isEditing ? 'Güncelleniyor...' : 'Kaydediliyor...'}
            </>
          ) : (
            <>{isEditing ? 'Güncelle' : 'Kaydet'}</>
          )}
        </Button>
      </div>
    </Form>
  );

  const content = isModal ? formBody : <Container>{formBody}</Container>;

  return (
    <>
      <Toast
        bg={toastState.variant}
        show={toastState.show}
        onClose={hideToast}
        delay={2000}
        autohide
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 1055
        }}
      >
        <Toast.Body className="text-white">{toastState.message}</Toast.Body>
      </Toast>
      {content}
    </>
  );
};

export default ProjectForm;
